import { LineProgress, Typography } from '@/components/atoms'
import { ComponentColors, ThemeColor } from '@/enums'
import { EmptyCircle, TickCircle } from '@/icons'
import { RiskCheck, RiskChecklistResponse } from '@/types/location'
import { useModal, useToggable } from '@/utils'

import { ReactElement, cloneElement, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Column, Row } from '../../styled'
import { BusinessInformationFlow } from './components/BusinessInformationFlow/BusinessInformationFlow'
import {
  ConnectAccountButton,
  HasBoughtARecommendationButton,
  InsuranceButtons,
  OnboardingButton,
  PoaButton,
  prepareChecklist,
} from './helpers'
import { Container, MainCollapseTopContent } from './styles'

interface Messages {
  title: string
  checklistItems: Record<string, any>
  onboardingButtonText: string
  signPOAButtonText: string
  addContractButtonText: string
  noContractsButtonText: string
  connectAccountButtonText: string
  hasBoughtARecommendationButtonText: string
}

const useMessages = (): Messages => {
  const { t } = useTranslation()

  return {
    title: t('riskChecklist.title'),
    checklistItems: t('riskChecklist.checklistItems', { returnObjects: true }),
    onboardingButtonText: t('riskChecklist.onboardingButtonText'),
    signPOAButtonText: t('riskChecklist.signPOAButtonText'),
    addContractButtonText: t('riskChecklist.addContractButtonText'),
    noContractsButtonText: t('riskChecklist.noContractsButtonText'),
    connectAccountButtonText: t('riskChecklist.connectAccountButtonText'),
    hasBoughtARecommendationButtonText: t(
      'riskChecklist.hasBoughtARecommendationButtonText',
    ),
  }
}

interface ChecklistItemProps {
  title?: string
  text?: string
  isDone?: boolean
  actionButtons?: ReactElement
  open?: boolean
  type?: string
}

const ChecklistItem = ({
  title,
  text,
  isDone = true,
  actionButtons,
  open,
}: ChecklistItemProps): ReactElement => {
  const [isOpen, setIsOpen] = useToggable(open)
  const Buttons = actionButtons
    ? cloneElement(actionButtons, {
        // @ts-expect-error cloning a custom component
        disabled: isDone,
      })
    : null

  useEffect(() => {
    setIsOpen(Boolean(open))
  }, [open, setIsOpen])

  return (
    <Row gap="16px">
      <div>
        {isDone ? (
          <TickCircle height={24} width={24} />
        ) : (
          <EmptyCircle height={24} width={24} />
        )}
      </div>
      <div>
        <Column gap="16px">
          <Column gap="4px">
            <Typography
              bold
              variant="p2Body"
              color={isDone ? ThemeColor.glass300 : ThemeColor.b100}
            >
              {title}
            </Typography>
            {text && isOpen && (
              <Typography color={ThemeColor.b50} variant="p2Body">
                {text}
              </Typography>
            )}
          </Column>
          {isOpen && Buttons}
        </Column>
      </div>
    </Row>
  )
}

export interface ChecklistWidgetBaseProps {
  steps: RiskCheck[]
  progress: number
}

export const ChecklistWidgetBase = ({
  steps,
  progress,
}: ChecklistWidgetBaseProps): ReactElement => {
  const messages = useMessages()

  return (
    <Container>
      <Typography bold variant="h5">
        {messages.title}
      </Typography>
      <MainCollapseTopContent>
        <LineProgress
          color={ComponentColors.success}
          percentage={progress * 100 >= 10 ? progress * 100 : 10}
        />
      </MainCollapseTopContent>
      <Column gap="16px">
        {steps.map((item) => (
          <ChecklistItem key={item.type} {...item} />
        ))}
      </Column>
    </Container>
  )
}

interface ChecklistWidgetProps {
  data?: RiskChecklistResponse
  locationId?: string
  companyId?: string
}

export const ChecklistWidget = ({
  data,
  locationId = '',
  companyId = '',
}: ChecklistWidgetProps): ReactElement => {
  const messages = useMessages()
  const [childOpen, setChildOpen] = useState('')
  const {
    open: openBusinessInformationFlow,
    close: closeBusinessInformationFlow,
    isOpen: isBusinessInformationFlowOpen,
  } = useModal()

  const stepButtons = useMemo(
    () => ({
      onboarding: (
        <OnboardingButton
          locationId={locationId}
          text={messages.onboardingButtonText}
          onClick={openBusinessInformationFlow}
        />
      ),
      signedPOA: (
        <PoaButton companyId={companyId} text={messages.signPOAButtonText} />
      ),
      contractAdded: (
        <InsuranceButtons
          locationId={locationId}
          text={{
            text1: messages.addContractButtonText,
            text2: messages.noContractsButtonText,
          }}
        />
      ),
      bankAccountAdded: (
        <ConnectAccountButton text={messages.connectAccountButtonText} />
      ),
      hasBoughtARecommendation: (
        <HasBoughtARecommendationButton
          locationId={locationId}
          text={messages.hasBoughtARecommendationButtonText}
        />
      ),
    }),
    [locationId, companyId, messages, openBusinessInformationFlow],
  )

  useEffect(() => {
    if (data) {
      const checks = data.checks || []
      const firstNotCompleted = checks.find((check) => !check.isDone)
      setChildOpen(firstNotCompleted?.type || '')
    }
  }, [JSON.stringify(data?.checks), data])

  if (!data) {
    return <></>
  }

  const steps = prepareChecklist(messages.checklistItems, data, stepButtons)

  const stepsWithOpen = steps.map((step) =>
    step.type === childOpen ? { ...step, open: true } : step,
  )

  return (
    <>
      <ChecklistWidgetBase progress={data.progress} steps={stepsWithOpen} />
      <BusinessInformationFlow
        companyId={companyId}
        isOpen={isBusinessInformationFlowOpen}
        onClose={closeBusinessInformationFlow}
      />
    </>
  )
}
